import { makeAutoObservable } from "mobx"
import Papa from "papaparse"
import { GapDialogStore } from "reusable/gap-dialog/GapDialogStore"
import CreateClientCSVImportFields from "./CreateClientCSVImportFields"

const lineBreakPattern = /\r\n|\n|\r/g

/** Counts the line breaks in a piece of text. */
function countLineBreaks(text: string) {
   return text.match(lineBreakPattern)?.length ?? 0
}

/** Creates a tracker that returns the 1-based file line each streamed CSV record starts on. */
function createCsvRecordStartLineTracker(contents: string) {
   let scannedUpTo = 0
   let lineBreaksBeforeCursor = 0

   return (params: { cursor: number, record: Record<string, string> }) => {
      lineBreaksBeforeCursor += countLineBreaks(contents.slice(scannedUpTo, params.cursor))
      scannedUpTo = params.cursor
      const endsWithLineBreak = /(\r\n|\n|\r)$/.test(contents.slice(Math.max(0, params.cursor - 2), params.cursor))
      const recordEndLine = lineBreaksBeforeCursor - (endsWithLineBreak ? 1 : 0) + 1
      const lineBreaksInsideRecord = Object.values(params.record).reduce((total, value) => total + countLineBreaks(String(value)), 0)
      return recordEndLine - lineBreaksInsideRecord
   }
}

export default class CreateClientCSVImportStore {
   sp
   parseErrors: Papa.ParseError[] = []

   constructor(sp: { gapDialogStore: GapDialogStore, onClientsImported?: () => void }) {
      this.sp = sp
      makeAutoObservable(this)
   }

   /** Parses the selected CSV in the browser and keeps its rows on the store. */
   onFilesValidated = async (files: File[]) => {
      const [file] = files
      if (!file) return
      const contents = await file.text()
      const rows: Record<string, string>[] = []
      const lineNumbers: number[] = []
      const parseErrors: Papa.ParseError[] = []
      const fieldNamesByRow: (string[] | undefined)[] = []
      const recordStartLine = createCsvRecordStartLineTracker(contents)
      Papa.parse<Record<string, string>>(contents, {
         header: true,
         skipEmptyLines: true,
         transformHeader: header => header.trim(),
         transform: value => value.trim(),
         step: rowResult => {
            rows.push(rowResult.data)
            lineNumbers.push(recordStartLine({ cursor: rowResult.meta.cursor, record: rowResult.data }))
            parseErrors.push(...rowResult.errors)
            fieldNamesByRow.push(rowResult.meta.fields)
         },
      })
      this.setParseResult({ rows, lineNumbers, fieldNames: fieldNamesByRow.at(-1), parseErrors })
   }

   /** Clears the parsed rows when the file is removed. */
   onLastFileRemoved = () => {
      this.setParseResult({ rows: [], lineNumbers: [], parseErrors: [], fieldNames: [] })
   }

   setParseResult = (params: { rows: Record<string, string>[], lineNumbers: number[], parseErrors: Papa.ParseError[], fieldNames: string[] | undefined }) => {
      this.parseErrors = params.parseErrors
      if (!this.parseErrors.length) {
         this.sp.gapDialogStore.setChildContent(CreateClientCSVImportFields, {
            title: 'Map CSV fields',
            subTitle: 'Match each colum from your CSV to a client field',
            primaryButton: {
               name: 'Import'
            },
            data: {
               rows: params.rows,
               lineNumbers: params.lineNumbers,
               onClientsImported: this.sp.onClientsImported,
               fieldNames: params.fieldNames,
            }
         })
      }
   }
}
